import React, { useState } from "react";
import { Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/apiClient";

/**
 * MakeOfferModal — v3.99d.300 Phase 1.
 *
 * Modal for submitting a private monetary offer on an Offer Only
 * listing. Server is the authority on price, quantity, and minimum
 * offer; this modal performs friendly client-side bounds so the
 * "Submit Offer" button doesn't fire HTTP 422s for obvious typos.
 *
 * Props:
 *   listing   — the public listing doc
 *   onClose() — close handler
 *   onSubmitted(offer) — fired after a successful POST so the parent
 *     can flip its CTA into "Offer submitted" state.
 */
export default function MakeOfferModal({ listing, onClose, onSubmitted }) {
  const minOffer = Number(listing?.min_offer || 0);
  const suggested = Number(listing?.suggested_offer || 0);
  const maxQty = Math.max(0, Number(listing?.quantity || 0) - Number(listing?.reserved_quantity || 0));

  const [price, setPrice] = useState(
    suggested > 0 ? String(suggested.toFixed(2)) : "");
  const [qty, setQty] = useState(1);
  const [msg, setMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const numericPrice = Number(price) || 0;
  const total = numericPrice * Math.max(1, Number(qty) || 1);
  const belowMin = minOffer > 0 && numericPrice > 0 && numericPrice < minOffer;
  const qtyOverflow = qty > maxQty;
  const canSubmit = numericPrice >= 1 && qty >= 1 && !belowMin && !qtyOverflow && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const r = await api.post(`/listings/${listing.id}/offers`, {
        quantity: Number(qty),
        offer_price_per_unit: numericPrice,
        customer_message: msg.trim() || undefined,
      });
      toast.success("Offer submitted. You'll be notified when admin responds.");
      onSubmitted?.(r.data);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.7)" }}
          onClick={onClose}
          data-testid="make-offer-modal">
      <div className="relative w-full max-w-md border p-6"
            style={{
              background: "var(--surface)",
              borderColor: "var(--border)",
              borderRadius: 2,
            }}
            onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
                data-testid="make-offer-close"
                className="absolute top-3 right-3 p-1"
                aria-label="Close">
          <X className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4" style={{ color: "var(--gold)" }} />
          <span className="text-[10px] font-mono uppercase tracking-[0.22em]"
                style={{ color: "var(--gold)" }}>
            Private offer
          </span>
        </div>
        <h2 className="font-display text-2xl tracking-tight mb-1"
            style={{ color: "var(--text)" }}>
          Make an offer
        </h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>
          <span className="font-semibold">{listing?.title}</span>
          {listing?.from_house_collection && (
            <span className="ml-2 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 border"
                   style={{ color: "var(--gold)", borderColor: "rgba(212,175,55,0.5)" }}>
              House Collection
            </span>
          )}
        </p>

        {minOffer > 0 && (
          <p className="text-[12px] mb-4"
              style={{ color: "var(--text-muted)" }}
              data-testid="make-offer-min-hint">
            Minimum offer: ${minOffer.toFixed(2)} per unit.
          </p>
        )}

        <label className="block mb-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1"
                style={{ color: "var(--text-muted)" }}>
            Your offer per unit (USD)
          </div>
          <input type="number" inputMode="decimal" min="1" step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  data-testid="make-offer-price"
                  className="w-full bg-transparent border px-3 py-2 text-base font-mono outline-none"
                  style={{ borderColor: belowMin ? "rgb(220,80,60)" : "var(--border)",
                            color: "var(--text)", borderRadius: 2 }} />
          {belowMin && (
            <div className="mt-1 text-[11px]"
                  style={{ color: "rgb(220,80,60)" }}
                  data-testid="make-offer-below-min">
              Below the listing&apos;s minimum offer.
            </div>
          )}
        </label>

        <label className="block mb-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1"
                style={{ color: "var(--text-muted)" }}>
            Quantity (max {maxQty})
          </div>
          <input type="number" min="1" max={maxQty} step="1"
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, Math.min(maxQty, Number(e.target.value) || 1)))}
                  data-testid="make-offer-qty"
                  className="w-full bg-transparent border px-3 py-2 text-base font-mono outline-none"
                  style={{ borderColor: qtyOverflow ? "rgb(220,80,60)" : "var(--border)",
                            color: "var(--text)", borderRadius: 2 }} />
        </label>

        <label className="block mb-3">
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-1"
                style={{ color: "var(--text-muted)" }}>
            Message to admin (optional)
          </div>
          <textarea value={msg}
                     onChange={(e) => setMsg(e.target.value.slice(0, 1000))}
                     placeholder="A brief note about your offer…"
                     data-testid="make-offer-message"
                     rows={3}
                     className="w-full bg-transparent border px-3 py-2 text-sm outline-none resize-none"
                     style={{ borderColor: "var(--border)",
                               color: "var(--text)", borderRadius: 2 }} />
        </label>

        <div className="flex items-center justify-between border-t pt-4 mt-2"
              style={{ borderColor: "var(--border)" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em]"
                  style={{ color: "var(--text-muted)" }}>
              Total offer
            </div>
            <div className="font-display text-xl"
                  style={{ color: "var(--text)" }}
                  data-testid="make-offer-total">
              ${total.toFixed(2)}
            </div>
          </div>
          <button onClick={submit}
                  disabled={!canSubmit}
                  data-testid="make-offer-submit"
                  className="text-xs font-mono uppercase tracking-wider px-4 py-2 border inline-flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: "var(--gold)", color: "#000",
                            borderColor: "var(--gold)", borderRadius: 2 }}>
            {submitting && <Loader2 className="w-3 h-3 animate-spin" />}
            Submit offer
          </button>
        </div>

        <p className="text-[11px] mt-4 leading-relaxed"
            style={{ color: "var(--text-muted)" }}>
          Submitting an offer does not guarantee acceptance. Admin will
          review and respond. You&apos;ll see the response in your
          account under <span className="font-mono">My Offers</span>.
          {" "}If accepted, you&apos;ll have{" "}
          <span style={{ color: "var(--gold)" }}>
            {Number(listing?.accepted_offer_payment_hours || 24)} hours
          </span>{" "}
          to complete checkout — payment is always due within 24 hours,
          and at checkout you can use <b>Add to Existing Shipment</b>
          to combine eligible paid orders into one shipment with no
          additional shipping.
        </p>
      </div>
    </div>
  );
}
